import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/shared/lib/supabase';
import type { FutebolJogoDia, FutebolPartidaAtiva } from '@/shared/types';
import { preservarTempoJogo } from '../../domain/usecases/partida';
import { futebolRepository } from '../../data/futebolRepository';

export interface FutebolRealtimeCallbacks {
  onPartidaUpdate?: (partida: FutebolPartidaAtiva) => void;
}

export interface FutebolRealtimeState {
  partida: FutebolPartidaAtiva | null;
  jogo:    FutebolJogoDia | null;
  loading: boolean;
}

/**
 * Monitora `futebol_partida_ativa` via Realtime.
 *
 * - `partida`: atualizada instantaneamente via postgres_changes UPDATE (score, tempo, evento).
 * - `jogo`:    dados estáticos do jogo (nomes/logos dos times), carregados 1× quando
 *              fixture_id muda — times não mudam durante a partida.
 *
 * Padrão cbRef: evita stale closure em TVs que ficam 24/7 sem reload.
 */
let futebolChannelSeq = 0;

export function useFutebolRealtime(
  telaId: string,
  callbacks: FutebolRealtimeCallbacks = {},
): FutebolRealtimeState {
  const [partida, setPartida] = useState<FutebolPartidaAtiva | null>(null);
  const [jogo,    setJogo]    = useState<FutebolJogoDia | null>(null);
  const [loading, setLoading] = useState(true);

  // Nome único por instância: supabase.channel(topic) reutiliza o mesmo objeto
  // após subscribe(); um segundo .on() no mesmo topic lança erro.
  const channelNameRef = useRef(
    `futebol-partida-ativa-${++futebolChannelSeq}`,
  );

  const cbRef = useRef(callbacks);
  useEffect(() => { cbRef.current = callbacks; });

  // Carrega metadados do jogo sempre que fixture_id muda (ou aparece pela primeira vez)
  useEffect(() => {
    if (!partida?.fixture_id) { setJogo(null); return; }
    let cancelado = false;
    futebolRepository.buscarJogoPorFixtureId(partida.fixture_id).then(j => {
      if (!cancelado) setJogo(j);
    }).catch(() => {});
    return () => { cancelado = true; };
  }, [partida?.fixture_id]);

  useEffect(() => {
    if (!telaId) {
      setPartida(null);
      setLoading(false);
      return;
    }

    let cancelado = false;
    setLoading(true);

    const carregar = async () => {
      try {
        const data = await futebolRepository.buscarPartidaAtiva(telaId);
        if (!cancelado) setPartida(data);
      } finally {
        if (!cancelado) setLoading(false);
      }
    };

    carregar();

    const channel = supabase
      .channel(channelNameRef.current)
      .on(
        'postgres_changes',
        {
          event:  'UPDATE',
          schema: 'public',
          table:  'futebol_partida_ativa',
          filter: `tela_id=eq.${telaId}`,
        },
        (payload) => {
          if (cancelado) return;
          const novaPartida = payload.new as FutebolPartidaAtiva;
          setPartida(prev => {
            let merged = novaPartida;
            // Guard anti-regressão só se aplica DENTRO da mesma partida (mesmo fixture_id).
            // Quando um novo jogo é ativado, a RPC reseta tempo_jogo=NULL de propósito —
            // se comparássemos contra o tempo_jogo da partida anterior (ex: 'FT'), o guard
            // interpretaria o reset como "sem novidade" e manteria o relógio do jogo antigo.
            if (prev && prev.fixture_id === novaPartida.fixture_id) {
              const tempo = preservarTempoJogo(prev.tempo_jogo, novaPartida.tempo_jogo);
              if (tempo !== novaPartida.tempo_jogo) {
                merged = { ...novaPartida, tempo_jogo: tempo ?? novaPartida.tempo_jogo };
              }
            }
            cbRef.current.onPartidaUpdate?.(merged);
            return merged;
          });
        },
      )
      // Re-fetch em todo rejoin do canal: na TV Box a página não dispara
      // `visibilitychange`, então o catch-up do foreground abaixo nunca roda e
      // um placar que mudou enquanto o socket estava mudo só se corrigia no
      // UPDATE seguinte (poll de 30 s do cron).
      .subscribe((status) => {
        if (status === 'SUBSCRIBED' && !cancelado) carregar();
      });

    // Re-fetch ao retornar ao foreground: evita estado obsoleto quando o jogo
    // termina enquanto o app estava em background e o evento UPDATE foi perdido.
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && !cancelado) carregar();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      cancelado = true;
      supabase.removeChannel(channel);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [telaId]);

  return { partida, jogo, loading };
}
